import type { ITweetsResponseBody } from '../../../types/response/ITweetsResponseBody';
import { SimpleArtist } from './SimpleArtist';
import { SimpleMember } from './SimpleMember';

export class Tweet {
  constructor(
    private readonly _id: string,
    private readonly _content: string,
    private readonly _authorId: string,
    private readonly _authorName: string,
    private readonly _authoredDate: string,
    private readonly _createdAt: Date,
    private readonly _imageUrls: string[],
    private readonly _tweetUrl: string,
    private readonly _hashTags: string[] | null,
    private readonly _artist: SimpleArtist | null,
    private readonly _members: SimpleMember[] | null
  ) {}

  get id(): string {
    return this._id;
  }

  get content(): string {
    return this._content;
  }

  get authorId(): string {
    return this._authorId;
  }

  get authorName(): string {
    return this._authorName;
  }

  get authoredDate(): string {
    return this._authoredDate;
  }

  get createdAt(): Date {
    return this._createdAt;
  }

  get imageUrls(): string[] {
    return [...this._imageUrls];
  }

  get tweetUrl(): string {
    return this._tweetUrl;
  }

  get hashTags(): string[] {
    return this._hashTags || [];
  }

  get artist(): SimpleArtist | null {
    return this._artist;
  }

  get members(): SimpleMember[] {
    return this._members || [];
  }

  public getArtistMemberNames(): string {
    if (this._members && this._members.length > 0) {
      const memberNames = this._members.map((member) => member.name).filter((name) => name !== '');
      return (
        memberNames.slice(0, 2).join(', ') +
        (memberNames.length > 2 ? ` 외 ${memberNames.length - 2}명` : '')
      );
    }
    return this._artist?.name || '';
  }

  public getFormattedDate(): string {
    return this._createdAt.toLocaleDateString();
  }

  public hasImages(): boolean {
    return this._imageUrls.length > 0;
  }

  public baseImageUrl(): string {
    return this._imageUrls.length > 0 ? this._imageUrls[0].replace("'", '') : '';
  }

  public getContentPreview(maxLength = 100): string {
    if (this._content.length <= maxLength) return this._content;
    return `${this._content.substring(0, maxLength)}...`;
  }

  static fromResponse(response: ITweetsResponseBody): Tweet {
    return new Tweet(
      response.tweetId,
      response.text,
      '',
      response.author,
      response.authoredDate,
      new Date(response.authoredDate),
      response.imageUrls || [],
      response.tweetUrl,
      response.hashTags,
      response.artist ? SimpleArtist.fromResponse(response.artist) : null,
      response.members?.map((member) => SimpleMember.fromResponse(member)) || []
    );
  }
}
